import { Injectable } from '@angular/core';
import { Action, State, StateContext, createSelector } from '@ngxs/store';

import { FileUtil } from '../../../shared/util/file.util';
import {
  MedicalCaseDocumentUploadPhase,
  MedicalCasePhaseDocumentVersion,
  MedicalCasePhases,
} from '../../../domain/medical-case/model';
import {
  MedicalCasePreparationFileEntry,
  MedicalCasePreparationFilesActions,
} from './medical-case-preparation-files.actions';
import { MedicalCaseProcessingPhaseUtil } from '../util/medical-case-processing-phase.util';

export interface MedicalCasePreparationFilesStateModel {
  byCaseId: Record<string, MedicalCasePreparationFileEntry[]>;
}

@State<MedicalCasePreparationFilesStateModel>({
  name: 'medicalCasePreparationFiles',
  defaults: {
    byCaseId: {},
  },
})
@Injectable()
export class MedicalCasePreparationFilesState {
  static files(caseId: string, uploadPhase?: MedicalCaseDocumentUploadPhase | null) {
    return createSelector(
      [MedicalCasePreparationFilesState],
      (
        state: MedicalCasePreparationFilesStateModel
      ): MedicalCasePreparationFileEntry[] => {
        const entries = state.byCaseId?.[caseId] ?? [];
        if (uploadPhase == null) {
          return entries;
        }

        return entries.filter(entry => entry.uploadPhase === uploadPhase);
      }
    );
  }

  @Action(MedicalCasePreparationFilesActions.SeedFromCaseDetail)
  seedFromCaseDetail(
    ctx: StateContext<MedicalCasePreparationFilesStateModel>,
    {
      caseId,
      documents,
      phases,
    }: MedicalCasePreparationFilesActions.SeedFromCaseDetail
  ): void {
    const state = ctx.getState();
    const currentEntries = state.byCaseId?.[caseId] ?? [];
    const currentByDocumentId = new Map(
      currentEntries
        .filter(entry => entry.documentId != null)
        .map(entry => [entry.documentId as string, entry])
    );

    const localEntries = currentEntries.filter(entry => entry.documentId == null);

    const entries = documents.map(doc => {
      const existingEntry = currentByDocumentId.get(doc.id) ?? null;
      const uploadPhase = this.resolveUploadPhase(doc.originalFileId, phases);
      const activeVersion = this.resolveActiveVersion(
        doc.originalFileId,
        phases,
        uploadPhase
      );
      const include = existingEntry?.include ?? true;
      const fileName = activeVersion?.fileName?.trim() || doc.name;
      const documentCategory = activeVersion?.category ?? null;
      const documentDate = activeVersion?.documentDate ?? null;
      const versionId = activeVersion?.id?.trim() ?? existingEntry?.versionId ?? null;

      return {
        clientId: `${doc.id}`,
        fileName,
        originalFileName: doc.originalDocumentName?.trim() || doc.name,
        size: null,
        extension: FileUtil.getExtension(doc.originalDocumentName?.trim() || doc.name),
        url:
          doc.originalFileId != null
            ? `/medical-cases/${caseId}/files/${doc.originalFileId}`
            : null,
        fileId: doc.originalFileId,
        status: 'uploaded' as const,
        progress: 100,
        canDelete: true,
        documentId: doc.id,
        versionId,
        include,
        documentCategory,
        documentDate,
        metadataSaving: existingEntry?.metadataSaving ?? false,
        phaseStateOverride: null,
        uploadPhase,
      };
    });

    const nextEntries = [...entries, ...localEntries];

    ctx.patchState({
      byCaseId: {
        ...state.byCaseId,
        [caseId]: nextEntries,
      },
    });
  }

  private resolveActiveVersion(
    originalFileId: string,
    phases?: MedicalCasePhases,
    uploadPhase?: MedicalCaseDocumentUploadPhase | null
  ): MedicalCasePhaseDocumentVersion | null {
    if (phases == null) {
      return null;
    }

    const normalizedOriginalFileId = originalFileId.trim();
    if (!normalizedOriginalFileId) {
      return null;
    }

    const initPhases =
      uploadPhase != null
        ? [uploadPhase]
        : (['MEDICAL_RECORD_INIT', 'SECOND_OPINION_INIT'] as const);

    return (
      initPhases
        .flatMap(phase => phases[phase]?.documents ?? [])
        .find(document => {
          const documentOriginalFileId = document.originalDocument?.fileId?.trim();
          const documentActiveFileId = document.activeVersion?.fileId?.trim();

          return (
            documentOriginalFileId === normalizedOriginalFileId ||
            documentActiveFileId === normalizedOriginalFileId
          );
        })?.activeVersion ?? null
    );
  }

  @Action(MedicalCasePreparationFilesActions.AddLocalFile)
  addLocalFile(
    ctx: StateContext<MedicalCasePreparationFilesStateModel>,
    { caseId, entry }: MedicalCasePreparationFilesActions.AddLocalFile
  ): void {
    const state = ctx.getState();
    ctx.patchState({
      byCaseId: {
        ...state.byCaseId,
        [caseId]: [...(state.byCaseId?.[caseId] ?? []), entry],
      },
    });
  }

  @Action(MedicalCasePreparationFilesActions.UpdateProgress)
  updateProgress(
    ctx: StateContext<MedicalCasePreparationFilesStateModel>,
    {
      caseId,
      clientId,
      progress,
    }: MedicalCasePreparationFilesActions.UpdateProgress
  ): void {
    this.patchEntry(ctx, caseId, clientId, { progress });
  }

  @Action(MedicalCasePreparationFilesActions.MarkUploaded)
  markUploaded(
    ctx: StateContext<MedicalCasePreparationFilesStateModel>,
    {
      caseId,
      clientId,
      document,
    }: MedicalCasePreparationFilesActions.MarkUploaded
  ): void {
    const previewUrl = `/medical-cases/${caseId}/files/${document.originalFileId}`;
    this.patchEntry(ctx, caseId, clientId, {
      status: 'uploaded',
      progress: 100,
      canDelete: true,
      documentId: document.id,
      fileId: document.originalFileId,
      url: previewUrl,
      phaseStateOverride: null,
    });
  }

  private resolveUploadPhase(
    originalFileId: string,
    phases?: MedicalCasePhases
  ): MedicalCaseDocumentUploadPhase | null {
    if (phases == null) {
      return null;
    }

    const normalizedOriginalFileId = originalFileId.trim();
    if (!normalizedOriginalFileId) {
      return null;
    }

    const initPhases: readonly MedicalCaseDocumentUploadPhase[] = [
      'MEDICAL_RECORD_INIT',
      'SECOND_OPINION_INIT',
    ];

    return (
      initPhases.find(phase =>
        (phases[phase]?.documents ?? []).some(document => {
          const documentOriginalFileId = document.originalDocument?.fileId?.trim();
          const documentActiveFileId = document.activeVersion?.fileId?.trim();

          return (
            documentOriginalFileId === normalizedOriginalFileId ||
            documentActiveFileId === normalizedOriginalFileId
          );
        })
      ) ?? null
    );
  }

  @Action(MedicalCasePreparationFilesActions.MarkError)
  markError(
    ctx: StateContext<MedicalCasePreparationFilesStateModel>,
    { caseId, clientId }: MedicalCasePreparationFilesActions.MarkError
  ): void {
    this.patchEntry(ctx, caseId, clientId, {
      status: 'error',
      canDelete: true,
    });
  }

  @Action(MedicalCasePreparationFilesActions.SetUploading)
  setUploading(
    ctx: StateContext<MedicalCasePreparationFilesStateModel>,
    { caseId, clientId }: MedicalCasePreparationFilesActions.SetUploading
  ): void {
    this.patchEntry(ctx, caseId, clientId, {
      status: 'uploading',
      progress: 0,
      canDelete: false,
      documentId: null,
      url: null,
      phaseStateOverride: null,
    });
  }

  @Action(MedicalCasePreparationFilesActions.Remove)
  remove(
    ctx: StateContext<MedicalCasePreparationFilesStateModel>,
    { caseId, clientId }: MedicalCasePreparationFilesActions.Remove
  ): void {
    const state = ctx.getState();
    const current = state.byCaseId?.[caseId] ?? [];
    const next = current.filter(
      entry => entry.clientId !== clientId && entry.documentId !== clientId
    );

    ctx.patchState({
      byCaseId: {
        ...state.byCaseId,
        [caseId]: next,
      },
    });
  }

  @Action(MedicalCasePreparationFilesActions.UpdateMetadata)
  updateMetadata(
    ctx: StateContext<MedicalCasePreparationFilesStateModel>,
    {
      caseId,
      clientId,
      patch,
    }: MedicalCasePreparationFilesActions.UpdateMetadata
  ): void {
    this.patchEntry(ctx, caseId, clientId, patch);
  }

  @Action(MedicalCasePreparationFilesActions.SetMetadataSaving)
  setMetadataSaving(
    ctx: StateContext<MedicalCasePreparationFilesStateModel>,
    {
      caseId,
      clientId,
      pending,
    }: MedicalCasePreparationFilesActions.SetMetadataSaving
  ): void {
    this.patchEntry(ctx, caseId, clientId, { metadataSaving: pending });
  }

  @Action(MedicalCasePreparationFilesActions.ClearCase)
  clearCase(
    ctx: StateContext<MedicalCasePreparationFilesStateModel>,
    { caseId }: MedicalCasePreparationFilesActions.ClearCase
  ): void {
    const state = ctx.getState();
    const { [caseId]: removed, ...rest } = state.byCaseId;
    void removed;
    ctx.patchState({ byCaseId: rest });
  }

  private patchEntry(
    ctx: StateContext<MedicalCasePreparationFilesStateModel>,
    caseId: string,
    clientId: string,
    patch: Partial<MedicalCasePreparationFileEntry>
  ): void {
    const state = ctx.getState();
    const entries = state.byCaseId?.[caseId] ?? [];
    ctx.patchState({
      byCaseId: {
        ...state.byCaseId,
        [caseId]: entries.map(entry =>
          entry.clientId === clientId ? { ...entry, ...patch } : entry
        ),
      },
    });
  }
}
